import Close from "@mui/icons-material/Close";
import Edit from "@mui/icons-material/Edit";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Help from "@mui/icons-material/Help";
import PlayArrow from "@mui/icons-material/PlayArrow";
import Warning from "@mui/icons-material/Warning";
import {
	Box, Button, Dialog, Grid,
	IconButton, Input, InputLabel,
	Select, Stack, styled,
	TableCell, tableCellClasses, TableContainer,
	TableRow, Theme, Tooltip, tooltipClasses,
	Typography
} from "@mui/material";
import type { TooltipProps } from "@mui/material/Tooltip";
import { DataGridPro } from "@mui/x-data-grid-pro";

import { NAV_DRAWER_TRANSITION_MS } from "../../navigation/styled/sidebar-navigation.styled";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const SIDEBAR_OFFSET_VAR = "var(--sidebar-offset, 338px)";
const SIDEBAR_WIDTH_CALC = `calc(100% - (${SIDEBAR_OFFSET_VAR} + 20px))`;

/** Matches `SidebarNav` drawer width transition so main content tracks the drawer smoothly. */
const sidebarLayoutTransition = (theme: Theme) =>
	theme.transitions.create(["left", "width"], {
		easing: theme.transitions.easing.easeInOut,
		duration: NAV_DRAWER_TRANSITION_MS,
	});

export const CoAppMultiDropdownMenuProps = {
	slotProps: {
		paper: {
			style: {
				maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
				width: 250,
			}
		}
	},
};

// Section heading inside dialogs / settings cards.
export const CoAppH2 = styled(Typography)(({ theme }) => ({
	padding: theme.spacing(0.5, 0),
	textAlign: "left",
	color: "rgba(13, 31, 48, 0.92)",
	border: 0,
	fontSize: "1.125rem",
	fontWeight: 600,
	letterSpacing: "-0.005em",
	lineHeight: 1.4,
	display: "inline"
}));

// Subtitle / smaller heading (often used inside tooltips, inline labels).
export const CoAppH4 = styled(Typography)(() => ({
	margin: 0,
	textAlign: "center",
	fontWeight: 500,
	fontSize: "1rem",
	lineHeight: 1.5,
	letterSpacing: 0,
	padding: "4px 6px",
	color: "rgba(13, 31, 48, 0.68)"
}));

// Smallest of the H-series — caption-class labels, dialog meta text.
export const CoAppH5 = styled(Typography)(() => ({
	margin: 0,
	textAlign: "center",
	fontWeight: 500,
	fontSize: "0.75rem",
	lineHeight: 1.5,
	letterSpacing: "0.02em",
	padding: "4px 6px",
	color: "rgba(13, 31, 48, 0.55)"
}));

// Main data grid (rule library, users, roles, groups, plugins, workstations).
// Refined for brand fidelity: soft hover, pure-white surface, polished
// scrollbars, brand-teal selection state, no harsh borders.
export const CoAppDataGrid = styled(DataGridPro, {
	shouldForwardProp: (prop) => prop !== "isscim",
})<{ isscim?: string }>(({ theme, isscim = "false" }) => ({
	background: "#FFFFFF",
	borderBottomLeftRadius: isscim === "false" ? 16 : 0,
	borderBottomRightRadius: isscim === "false" ? 16 : 0,
	flexGrow: 1,
	border: "none",
	fontSize: 14,

	// Force every surface (root, scroller, pinned sections, cells, empty filler)
	// to pure white so the grid reads as one continuous canvas. MUI X paints
	// the pinned-left section + filler with a subtle grey by default — wipe it
	// and remove the right-side shadow it puts under the pinned column block.
	"&, & .MuiDataGrid-main, & .MuiDataGrid-virtualScroller, & .MuiDataGrid-virtualScrollerContent, & .MuiDataGrid-virtualScrollerRenderZone, & .MuiDataGrid-pinnedColumns, & .MuiDataGrid-pinnedColumns--left, & .MuiDataGrid-pinnedColumns--right, & .MuiDataGrid-filler, & .MuiDataGrid-filler--pinnedLeft, & .MuiDataGrid-filler--pinnedRight, & .MuiDataGrid-scrollbarFiller, & .MuiDataGrid-scrollbarFiller--header, & .MuiDataGrid-scrollbarFiller--pinnedLeft, & .MuiDataGrid-scrollbarFiller--pinnedRight, & .MuiDataGrid-detailPanels, & .MuiDataGrid-overlay, & .MuiDataGrid-overlayWrapper, & .MuiDataGrid-overlayWrapperInner, & .MuiDataGrid-cell, & .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight": {
		background: "#FFFFFF !important",
		backgroundColor: "#FFFFFF !important"
	},
	// Kill the drop-shadow MUI X paints on the right edge of the pinned-left
	// block — it reads as a vertical grey smudge on a white-on-white grid.
	"& .MuiDataGrid-pinnedColumns--left, & .MuiDataGrid-pinnedColumnHeaders--left, & .MuiDataGrid-cell--pinnedLeft": {
		boxShadow: "none !important",
		borderRight: "none !important"
	},
	"& .MuiDataGrid-pinnedColumns--right, & .MuiDataGrid-pinnedColumnHeaders--right, & .MuiDataGrid-cell--pinnedRight": {
		boxShadow: "none !important",
		borderLeft: "none !important"
	},
	// MUI X v9 renamed the pinned-section classes: pinned columns are now
	// inline cells/headers, and the divider between the pinned (checkbox /
	// actions) sections and the scrollable area is drawn as a left/right
	// border on the pinned HEADER cells + the header scrollbar filler. The
	// rules above use the old v6 container names (pinnedColumnHeaders--*),
	// which no longer match — so these hairlines reappeared in the column
	// header row only. Strip the vertical borders off the v9 pinned/header
	// elements to match the borderless body.
	"& .MuiDataGrid-columnHeader--pinnedLeft, & .MuiDataGrid-columnHeader--pinnedRight, & .MuiDataGrid-columnHeader--withLeftBorder, & .MuiDataGrid-columnHeader--withRightBorder, & .MuiDataGrid-cell--withLeftBorder, & .MuiDataGrid-cell--withRightBorder, & .MuiDataGrid-filler--pinnedLeft, & .MuiDataGrid-filler--pinnedRight, & .MuiDataGrid-scrollbarFiller, & .MuiDataGrid-scrollbarFiller--pinnedRight": {
		borderLeft: "none !important",
		borderRight: "none !important",
		boxShadow: "none !important"
	},
	// v9 paints the pinned-section drop shadow as a ::before/::after pseudo on
	// the pinned cells/headers (NOT a box-shadow on the element itself), so the
	// rules above didn't catch it — it survived as a soft framed "panel"
	// (the ghost rectangle) around the scrollable columns. Strip the pseudo's
	// shadow only; the cell keeps its own white background so the sticky
	// columns still cover content scrolling underneath them.
	"& .MuiDataGrid-cell--pinnedLeft::before, & .MuiDataGrid-cell--pinnedLeft::after, & .MuiDataGrid-cell--pinnedRight::before, & .MuiDataGrid-cell--pinnedRight::after, & .MuiDataGrid-columnHeader--pinnedLeft::before, & .MuiDataGrid-columnHeader--pinnedLeft::after, & .MuiDataGrid-columnHeader--pinnedRight::before, & .MuiDataGrid-columnHeader--pinnedRight::after": {
		boxShadow: "none !important"
	},
	// Container-level scroll shadows v9 toggles via these modifiers (the soft
	// edge that appears once the grid scrolls horizontally).
	"&.MuiDataGrid-root--hasScrollX .MuiDataGrid-main, & .MuiDataGrid-main--hasPinnedRight, & .MuiDataGrid-main--hasPinnedLeft": {
		boxShadow: "none !important"
	},

	// Headers — flat pure-white strip with a hairline below. No subtle grey
	// fill, no vertical column separators, no per-cell borders.
	"& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeadersInner, & .MuiDataGrid-columnHeader--filledGroup, & .MuiDataGrid-pinnedColumnHeaders, & .MuiDataGrid-pinnedColumnHeaders--left, & .MuiDataGrid-pinnedColumnHeaders--right, & .MuiDataGrid-topContainer, & .MuiDataGrid-topContainer--top": {
		background: "#FFFFFF !important",
		backgroundColor: "#FFFFFF !important",
		boxShadow: "none !important",
		borderBottom: "none !important"
	},
	"& .MuiDataGrid-columnHeaders": {
		borderBottom: "1px solid rgba(13, 31, 48, 0.08) !important"
	},
	"& .MuiDataGrid-columnHeader": {
		borderBottom: isscim === "true" ? "3px solid rgba(13, 31, 48, 0.12)" : "none",
		"&:focus, &:focus-within": { outline: "none" }
	},
	// Keep exactly ONE hairline under the column headers. v9 also paints a
	// bottom border on the header row's filler + scrollbar-filler (via its
	// row--borderBottom mechanism); left in place they stack a second line
	// against our single .MuiDataGrid-columnHeaders divider, which reads as a
	// redundant/doubled top line. Suppress them here.
	"& .MuiDataGrid-columnHeaders .MuiDataGrid-filler, & .MuiDataGrid-columnHeaders .MuiDataGrid-scrollbarFiller": {
		borderBottom: "none !important"
	},
	"& .MuiDataGrid-columnHeaderTitle": {
		fontSize: 12,
		fontWeight: 600,
		color: "rgba(13, 31, 48, 0.55)",
		letterSpacing: "0.04em",
		textTransform: "uppercase"
	},

	// Rows — pure white at rest, teal tint on hover/selection.
	"& .MuiDataGrid-row": {
		background: "#FFFFFF",
		transition: "background 0.12s ease"
	},
	"& .MuiDataGrid-row.Mui-hovered, & .MuiDataGrid-row:hover": {
		background: "rgba(2, 213, 171, 0.04)"
	},
	"& .MuiDataGrid-row.Mui-selected": {
		background: "rgba(2, 213, 171, 0.08)",
		"&:hover": { background: "rgba(2, 213, 171, 0.12)" }
	},

	// Cells. NOTE: MUI X v9 draws row separators as a `borderTop` on each
	// cell (var(--DataGrid-rowBorderColor)). We draw our own single hairline
	// via borderBottom below, so the default borderTop must be removed —
	// otherwise every row boundary stacks two hairlines (the cell's top
	// border + the previous row's bottom border), which read as doubled/
	// redundant lines, most visible just under the column header.
	"& .MuiDataGrid-cell": {
		borderTop: "none",
		borderBottom: "1px solid rgba(13, 31, 48, 0.05)",
		fontSize: 14,
		color: "rgba(13, 31, 48, 0.85)",
		"&:focus, &:focus-within": { outline: "none" }
	},

	// Kill-switch row variants (org settings)
	"& .MuiDataGrid-row.CustomKillSwitchRow--active": {
		background: "rgba(232, 80, 91, 0.06)"
	},
	"& .MuiDataGrid-row.CustomKillSwitchRow--active .MuiDataGrid-cell": {
		background: "rgba(232, 80, 91, 0.06) !important",
		backgroundColor: "rgba(232, 80, 91, 0.06) !important"
	},
	"& .MuiDataGrid-row.CustomKillSwitchRow--active.Mui-hovered, & .MuiDataGrid-row.CustomKillSwitchRow--active:hover": {
		background: "rgba(232, 80, 91, 0.10)"
	},
	"& .MuiDataGrid-row.CustomKillSwitchRow--active.Mui-hovered .MuiDataGrid-cell, & .MuiDataGrid-row.CustomKillSwitchRow--active:hover .MuiDataGrid-cell": {
		background: "rgba(232, 80, 91, 0.10) !important",
		backgroundColor: "rgba(232, 80, 91, 0.10) !important"
	},
	"& .MuiDataGrid-row.CustomKillSwitchRow--active.Mui-selected": {
		background: "rgba(232, 80, 91, 0.14)"
	},
	"& .MuiDataGrid-row.CustomKillSwitchRow--active.Mui-selected .MuiDataGrid-cell": {
		background: "rgba(232, 80, 91, 0.14) !important",
		backgroundColor: "rgba(232, 80, 91, 0.14) !important"
	},

	// Location-with-trigger highlighted row (used in some scim/wizard tables)
	"& .MuiDataGrid-row.location-w-trigger": {
		background: "rgba(2, 213, 171, 0.10)",
		color: "rgba(13, 31, 48, 0.92)",
		"&:hover": { background: "rgba(2, 213, 171, 0.16)" },
		"&.Mui-selected": { background: "rgba(2, 213, 171, 0.20)" }
	},

	// Remove harsh internal borders
	"& .MuiDataGrid-cell--withRightBorder, & .MuiDataGrid-columnHeader--withRightBorder, & .MuiDataGrid-filler--pinnedLeft": {
		borderRight: "none"
	},
	// Remove the scroll-shadow vertical line MUI renders on the right edge
	"& .MuiDataGrid-scrollShadow--vertical": {
		borderRight: "none !important",
		boxShadow: "none !important"
	},

	// ── Filter panel ──────────────────────────────────────────────
	// Push each filter row's delete (X) button to the FAR RIGHT of the row
	// (MUI renders it first/left by default).
	"& .MuiDataGrid-filterForm": {
		alignItems: "center"
	},
	"& .MuiDataGrid-filterFormDeleteIcon": {
		order: 99,
		marginLeft: "auto",
		marginRight: 0
	},
	// Recolor the native date/time picker accent from the browser's default
	// blue to CoApp teal. Native form controls can't take a CSS gradient, so
	// this uses the solid brand color (closest possible to the gradient).
	"& .MuiDataGrid-panel input[type='datetime-local'], & .MuiDataGrid-panel input[type='date'], & .MuiDataGrid-panel input[type='time'], & .MuiDataGrid-filterFormValueInput input": {
		accentColor: "#02D5AB",
		colorScheme: "light"
	},
	// Any remaining MUI-primary (blue) accents inside the panel → CoApp teal.
	"& .MuiDataGrid-panel .MuiCheckbox-root.Mui-checked, & .MuiDataGrid-panel .MuiRadio-root.Mui-checked": {
		color: "#02D5AB"
	},
	// Filter panel footer: "Remove all" is destructive — render it red (text +
	// trash icon). "Add filter" (the first button) keeps its brand-teal color.
	"& .MuiDataGrid-panelFooter button:last-of-type": {
		color: theme.palette.error.main,
		"& .MuiSvgIcon-root": { color: theme.palette.error.main },
		"&:hover": { background: "rgba(224, 48, 48, 0.08)" }
	},

	// Reserve a permanent gutter for the vertical scrollbar so column content
	// never slides underneath it. Without this, autosizeColumns({expand:true})
	// fills the full client width measured BEFORE the vertical scrollbar
	// appears; once rows overflow and the 10px scrollbar shows, the columns are
	// ~10px too wide and the last column's text (e.g. the "AM"/"PM" of the Last
	// Compiled timestamps) bleeds under/right of the scrollbar. A stable gutter
	// makes clientWidth account for the scrollbar up front.
	"& .MuiDataGrid-virtualScroller": {
		scrollbarGutter: "stable"
	},
	// Scrollbars — match the rest of the app
	"& ::-webkit-scrollbar": {
		background: "#FFFFFF",
		width: 12,
		height: 12
	},
	"& ::-webkit-scrollbar-thumb": {
		borderRadius: 999,
		background: "rgba(2, 213, 171, 0.55)",
		border: "3px solid #FFFFFF",
		backgroundClip: "content-box",
		"&:hover": { background: "rgba(2, 213, 171, 0.85)", backgroundClip: "content-box" }
	}
}));

export const CoAppDataGridTextCellContainer = styled("div")(() => ({
	marginTop: "15px"
}));


export const CoAppDataGridToolbarButton = styled(Button)(() => ({
	backgroundColor: "#FFFFFF",
	color: "#2FBC70",
	textDecoration: "underline",
	margin: "0px 10px 0px 0px",
	float: "right",
	borderRadius: "10px",
	padding: "5px",
	":hover": {
		backgroundColor: "#E3E6EC",
	},
}));

export const CoAppGroupList = styled("div", {
	shouldForwardProp: (prop) => !["overflowing", "count", "superadmin"].includes(prop as string),
})<{ overflowing?: boolean; count?: number; superadmin?: string }>(({ theme, overflowing, count, superadmin }) => {
	if (overflowing) {
		return {
			fontSize: theme.typography.body2.fontSize,
			textWrap: "wrap",
			height: "50px",
			alignContent: "center",
			"&::after": {
				content: `" +${count}"`,
				color: theme.palette.info.main
			},
			lineHeight: "16px",
			color: superadmin === "true" ? theme.palette.text.disabled : theme.palette.text.primary
		};
	} else {
		return {
			fontSize: theme.typography.body2.fontSize,
			textWrap: "wrap",
			height: "50px",
			alignContent: "center",
			lineHeight: "16px",
			color: superadmin === "true" ? theme.palette.text.disabled : theme.palette.text.primary
		};
	}
});

export const CoAppSelect = styled(Select)(() => ({
	"&.MuiOutlinedInput-root": {
		"& fieldset": {
			border: "1px solid #A9A9A9"
		},
		"&:hover fieldset": {
			border: "1px solid #A9A9A9"
		},
		"&.Mui-focused fieldset": {
			border: "1px solid #A9A9A9"
		}
	},
	backgroundColor: "#FFFFFF",
	width: "250px",
	height: "50px"
}));

export const CoAppStack = styled(Stack)(() => ({
	margin: "auto",
	width: "95%"
}));

export const CoAppStackItem = styled("div")(() => ({
	borderRadius: "5px",
	backgroundColor: "#FFFFFF",
	boxShadow: "0px 2px 2px 2px #00000026",
	width: "auto",
	justifyContent: "flex-start",
	display: "flex",
	fontSize: "1rem",
	alignItems: "center",
	height: "69px",
	rowGap: "50px",
}));

export const CoAppInputLabel = styled(InputLabel)(() => ({
	"&.MuiInputLabel-root": {
		color: "#A9A9A9"
	}
}));

export const CoAppInlineInput = styled(Input)(() => ({
	color: "#A9A9A9",
	fontSize: "1.5rem",
	fontWeight: "600",
	marginRight: "50px",
}));

export const CoAppInlineInputHeader = styled(Typography)(({ theme }) => ({
	backgroundColor: "#F8F9FA",
	...theme.typography.body2,
	textAlign: "left",
	color: theme.palette.text.primary,
	border: 0,
	fontSize: "2.3rem",
	fontWeight: "600",
	"&:hover": {
		cursor: "pointer",
		color: "#7482A0"
	}
}));

export const CoAppMainContainer = styled("div")(({ theme }) => ({
	backgroundColor: "#F8F9FA",
	...theme.typography.body2,
	textAlign: "left",
	color: theme.palette.text.primary,
	border: 0,
	fontSize: "2.5rem",
	p: 2,
	margin: "auto",
	flexGrow: 1,
}));

// Cancel button used in modal footers. Outlined-light treatment so it sits
// beside a gradient primary action without competing.
export const CoAppCancelButton = styled(Button)(() => ({
	color: "rgba(13, 31, 48, 0.78)",
	background: "#FFFFFF",
	textTransform: "none",
	height: 40,
	fontSize: 14,
	fontWeight: 600,
	letterSpacing: "0.02em",
	borderRadius: 999,
	padding: "6px 18px",
	border: "1px solid rgba(13, 31, 48, 0.14)",
	transition: "background 0.18s, border-color 0.18s",
	"&:hover": {
		background: "rgba(13, 31, 48, 0.04)",
		borderColor: "rgba(13, 31, 48, 0.28)",
	}
}));

export const CoAppConfirmStackItemGroup = styled("div")(() => ({
	display: "flex",
	flexDirection: "column",
	width: "100%",
	margin: "auto",
	borderRadius: "5px",
}));

export const CoAppConfirmStackItemGroupTitle = styled("div", {
	shouldForwardProp: (prop) => prop !== "isfirst",
})<{ isfirst?: boolean }>((props) => ({
	fontSize: "1.5rem",
	fontWeight: "bold",
	marginBottom: "20px",
	marginTop: props.isfirst ? "0px" : "30px"
}));

export const CoAppConfirmContainer = styled("div")(() => ({
	display: "block",
	justifyContent: "center",
	alignItems: "center",
	width: "100%"
}));

export const CoAppConfirmStack = styled(Stack)(() => ({
	margin: "auto",
	borderRadius: "5px",
	boxShadow: "0px 2px 2px 2px #00000026",
	width: "100%",
	border: "1px solid #00000026",
}));

export const CoAppConfirmStackItemContent = styled("p", {
	shouldForwardProp: (prop) => prop !== "hasvalue",
})<{ hasvalue?: boolean }>((props) => ({
	width: !props.hasvalue ? "700px" : "200px",
	textOverflow: !props.hasvalue ? "none" : "ellipsis",
	whiteSpace: "nowrap",
	overflow: "hidden"
}));

export const CoAppConfirmStackItem = styled("div", {
	shouldForwardProp: (prop) => prop !== "itemtype",
})<{ itemtype?: string }>(({ itemtype }) => ({
	borderRadius: itemtype === "header" ? "5px 5px 0px 0px" : itemtype === "footer" ? "0px 0px 5px 5px" : "0px",
	backgroundColor: itemtype === "header" ? "#F7F8F9" : "#FFFFFF",
	width: "auto",
	position: "relative",
	display: "flex",
	fontSize: "1rem",
	alignItems: "center",
	height: "50px",
	borderBottom: "1px solid #00000026",
	paddingLeft: "15px",
	color: itemtype != null && ["header", "footer"].includes(itemtype) ? "#000000" : "#A2A2A2",
}));

// Back button on settings detail pages (e.g. user profile, group page).
export const CoAppSettingsBackButton = styled(Button)(() => ({
	borderRadius: 999,
	color: "rgba(13, 31, 48, 0.65)",
	textTransform: "none",
	fontSize: 14,
	fontWeight: 600,
	letterSpacing: "0.02em",
	height: 36,
	padding: "4px 14px",
	transition: "background 0.18s, color 0.18s",
	"&:hover": {
		background: "rgba(2, 213, 171, 0.08)",
		color: "#02D5AB"
	}
}));

export const CoAppBasicContainer = styled("div")(() => ({
	display: "flex",
	justifyContent: "center",
	alignItems: "center"
}));

// ── Login screen chrome ─────────────────────────────────────────────────────
//
// Centered white card on a brand teal-to-blue gradient background.
// Wraps the Descope <Descope flowId={...}> widget. Descope's internals are
// restyled via descendant selectors below.
// ─────────────────────────────────────────────────────────────────────────────

export const CoAppLoginContainer = styled("div")(({ theme }) => ({
	// Pure-white page background — same canvas as the rest of the app.
	background: "#FFFFFF",
	minHeight: "100vh",
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	justifyContent: "center",
	padding: "32px 16px",
	fontFamily: "Work Sans, -apple-system, BlinkMacSystemFont, sans-serif"
}));

export const CoAppLoginCard = styled("div")(({ theme }) => ({
	width: "100%",
	maxWidth: 440,
	background: "#FFFFFF",
	borderRadius: 16,
	// Flat — no card elevation, the whole login lives on a pure white plane.
	boxShadow: "none",
	padding: "40px 36px 32px",
	display: "flex",
	flexDirection: "column",
	alignItems: "stretch",
	gap: 24
}));

export const CoAppLoginLogo = styled("img")(({ theme }) => ({
	width: 168,
	height: "auto",
	margin: "0 auto 4px",
	display: "block",
	pointerEvents: "none",
	userSelect: "none"
}));

export const CoAppLoginFooter = styled("div")(({ theme }) => ({
	marginTop: 8,
	paddingTop: 20,
	borderTop: "1px solid rgba(13, 31, 48, 0.08)",
	display: "flex",
	justifyContent: "center",
	gap: 18,
	fontSize: 13,
	fontWeight: 500,
	"& a": {
		color: "rgba(13, 31, 48, 0.55)",
		textDecoration: "none",
		transition: "color 0.18s cubic-bezier(0.4, 0, 0.2, 1)",
		"&:hover": {
			color: "var(--coapp-text-interactive, #057A6D)"
		}
	},
	"& span": {
		color: "rgba(13, 31, 48, 0.25)"
	}
}));

export const CoAppLoginFlowContainer = styled("div")(({ theme }) => ({
	width: "100%",
	display: "flex",
	flexDirection: "column",
}));

export const CoAppCloseIcon = styled(Close, {
	shouldForwardProp: (prop) => !["top", "right", "left"].includes(prop as string),
})<{ top?: number; right?: number; left?: number }>(({ theme, top, right, left }) => ({
	position: "absolute",
	top: top ? top : 8,
	right: right ? right : 24,
	color: theme.palette.text.secondary,
	cursor: "pointer"
}));

// Text button — used for inline secondary actions ("Edit name", "Delete rule",
// link-style row actions). Accessible-teal text on transparent, soft hover wash.
// (#057A6D, 4.6:1 on white — meets WCAG AA for body text, where bare #02D5AB at
// 2.1:1 fails. The bright teal is reserved for backgrounds + icon surfaces.)
export const CoAppTextButton = styled(Button, {
	shouldForwardProp: (prop) => prop !== "ismodal" && prop !== "isrow",
})<{ ismodal?: string; isrow?: string }>(({ ismodal = "true", isrow = "false" }) => ({
	borderRadius: 8,
	color: "var(--coapp-text-interactive, #057A6D)",
	whiteSpace: "nowrap",
	textTransform: "none",
	fontSize: 14,
	fontWeight: ismodal === "true" || isrow === "true" ? 600 : 500,
	letterSpacing: "0.02em",
	padding: "6px 12px",
	transition: "background 0.18s, color 0.18s",
	":hover": {
		color: "#00B899",
		background: "rgba(2, 213, 171, 0.08)"
	},
	marginRight: 8
}));

// Destructive text button — Delete-flavored variant of CoAppTextButton.
export const CoAppTextDestructiveButton = styled(Button)(() => ({
	background: "transparent",
	borderRadius: 8,
	color: "#E03030",
	whiteSpace: "nowrap",
	textTransform: "none",
	fontSize: 14,
	fontWeight: 600,
	letterSpacing: "0.02em",
	padding: "6px 12px",
	transition: "background 0.18s, color 0.18s",
	":hover": {
		color: "#CC2C2A",
		background: "rgba(224, 48, 48, 0.08)"
	},
	marginRight: 8
}));

// Outlined cancel button — pairs with CoAppActionButton in modal footers.
export const CoAppCancelTextButton = styled(Button)(() => ({
	background: "#FFFFFF",
	border: "1px solid rgba(13, 31, 48, 0.14)",
	borderRadius: 999,
	color: "rgba(13, 31, 48, 0.78)",
	whiteSpace: "nowrap",
	minWidth: 104,
	justifyContent: "center",
	textTransform: "none",
	fontSize: 14,
	fontWeight: 600,
	letterSpacing: "0.02em",
	padding: "6px 18px",
	transition: "background 0.18s, border-color 0.18s",
	":hover": {
		color: "rgba(13, 31, 48, 0.92)",
		background: "rgba(13, 31, 48, 0.04)",
		borderColor: "rgba(13, 31, 48, 0.28)"
	},
	marginRight: 8
}));

// Destructive primary action — solid red pill for irreversible actions.
export const CoAppDestructiveActionButton = styled(Button)(() => ({
	color: "#FFFFFF",
	background: "#E03030",
	whiteSpace: "nowrap",
	minWidth: 104,
	justifyContent: "center",
	textTransform: "none",
	fontSize: 14,
	fontWeight: 600,
	letterSpacing: "0.02em",
	transition: "background 0.18s, box-shadow 0.18s, filter 0.18s",
	":hover": {
		background: "#CC2C2A",
		filter: "brightness(1.05)",
		boxShadow: "0 6px 18px rgba(224, 48, 48, 0.28), 0 2px 6px rgba(204, 44, 42, 0.20)"
	},
	":active": {
		filter: "brightness(0.96)"
	},
	borderRadius: 999,
	padding: "6px 18px",
	boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(224, 48, 48, 0.16)",
	"&.Mui-disabled": {
		color: "rgba(255, 255, 255, 0.72)",
		background: "rgba(224, 48, 48, 0.5)",
		boxShadow: "none"
	}
}));

// CoApp primary action button — gradient teal-to-blue with subtle teal glow.
// Used by `+ Build Rule`, `+ Add User`, `+ Add Group`, etc. across page headers.
// The glow is a soft outer ring of brand teal at low alpha — matches prototype.
export const CoAppActionButton = styled(Button)(() => ({
	color: "#FFFFFF",
	background: "var(--coapp-gradient-primary, linear-gradient(135deg, #02D5AB 0%, #0A7FA8 100%))",
	whiteSpace: "nowrap",
	minWidth: 104,
	justifyContent: "center",
	textTransform: "none",
	fontSize: "14px",
	fontWeight: "600",
	letterSpacing: "0.02em",
	transition: "filter 0.18s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.18s cubic-bezier(0.4, 0, 0.2, 1)",
	":hover": {
		background: "var(--coapp-gradient-primary, linear-gradient(135deg, #02D5AB 0%, #0A7FA8 100%))",
		filter: "brightness(1.06)",
		boxShadow: "0 0 0 4px rgba(2, 213, 171, 0.14), 0 6px 18px rgba(2, 213, 171, 0.32)"
	},
	":active": {
		filter: "brightness(0.96)"
	},
	borderRadius: "100px",
	padding: "6px 16px",
	// Subtle outer teal glow at rest — prototype's "halo" treatment.
	boxShadow: "0 0 0 3px rgba(2, 213, 171, 0.10), 0 4px 12px rgba(2, 213, 171, 0.20)",
	"& > svg": {
		fontSize: "16px",
		marginRight: "6px"
	},
	"&.Mui-disabled": {
		color: "rgba(255, 255, 255, 0.72)",
		background: "linear-gradient(135deg, rgba(2,213,171,0.5) 0%, rgba(10,127,168,0.5) 100%)",
		boxShadow: "none",
		":hover": {
			background: "linear-gradient(135deg, rgba(2,213,171,0.5) 0%, rgba(10,127,168,0.5) 100%)",
			filter: "none"
		}
	}
}));

// Inverted variant — outlined teal on white surface.
export const CoAppInvertedActionButton = styled(Button)(({ theme }) => ({
	color: theme.palette.primary.main,
	backgroundColor: "#FFFFFF",
	border: `1px solid ${theme.palette.primary.main}`,
	whiteSpace: "nowrap",
	minWidth: 104,
	justifyContent: "center",
	textTransform: "none",
	fontSize: "14px",
	fontWeight: "600",
	lineHeight: "20px",
	letterSpacing: "0.02em",
	transition: "background 0.18s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.18s cubic-bezier(0.4, 0, 0.2, 1)",
	":hover": {
		color: theme.palette.primary.dark,
		borderColor: theme.palette.primary.dark,
		backgroundColor: "rgba(2, 213, 171, 0.06)"
	},
	borderRadius: "100px",
	padding: "6px 16px"
}));

// CoApp tooltip — used everywhere in the app. Dark surface, soft shadow,
// brand-aware. Replaces the legacy washed-out white tooltip.
/** `styled(Tooltip)` with anonymous render loses MUI prop types; typed roots fix it once for all call sites. */
type CoAppStyledTooltipRootProps = TooltipProps & { className?: string };

function CoAppLightTooltipRoot({ className, ...props }: CoAppStyledTooltipRootProps) {
	return (
		<Tooltip
			{...props}
			arrow
			placement={props.placement ?? "bottom"}
			classes={{ popper: className }}
		/>
	);
}

export const CoAppLightTooltip = styled(CoAppLightTooltipRoot)(() => ({
	[`& .${tooltipClasses.tooltip}`]: {
		background: "rgba(13, 31, 48, 0.92)",
		color: "#FFFFFF",
		fontSize: 12,
		fontWeight: 500,
		letterSpacing: "0.01em",
		padding: "6px 10px",
		borderRadius: 6,
		boxShadow: "0 4px 12px rgba(13, 31, 48, 0.20), 0 1px 3px rgba(13, 31, 48, 0.10)",
		lineHeight: 1.45,
		maxWidth: 240
	},
	[`& .${tooltipClasses.arrow}`]: {
		color: "rgba(13, 31, 48, 0.92)"
	}
}));

// CoApp confirmation / form dialog. Soft shadow, rounded card,
// generous padding. Used for Create Rule wizard, delete confirmations,
// kill-switch toggles, etc.
export const CoAppDialog = styled(Dialog)(() => ({
	"& .MuiDialog-paper": {
		borderRadius: 16,
		boxShadow: "0 24px 64px rgba(13, 31, 48, 0.18), 0 0 0 1px rgba(13, 31, 48, 0.04)",
		padding: 0,
		overflow: "hidden"
	},
	"& .MuiBackdrop-root": {
		background: "rgba(13, 31, 48, 0.42)",
		backdropFilter: "blur(2px)"
	},
	"& .MuiDialogTitle-root": {
		fontSize: 18,
		fontWeight: 600,
		letterSpacing: "-0.005em",
		color: "rgba(13, 31, 48, 0.92)",
		padding: "20px 24px 12px"
	},
	"& .MuiDialogContent-root": {
		padding: "8px 24px"
	},
	"& .MuiDialogActions-root": {
		padding: "16px 24px 20px",
		gap: 8
	}
}));

export const CoAppDialogTooltipContainer = styled("div")(() => ({
	display: "block",
	padding: "4px"
}));

export const CoAppDialogTooltipText = styled("div", {
	shouldForwardProp: (prop) => prop !== "name",
})<{ name?: string }>(({ theme, name }) => ({
	color: name === "true" ? theme.palette.text.primary : theme.palette.text.secondary,
	fontSize: theme.typography.body3.fontSize,
	marginBottom: "-10px",
	marginTop: "-10px"
}));

export const CoAppWarningIcon = styled(Warning, {
	shouldForwardProp: (prop) => prop !== "withoutscroll",
})<{ withoutscroll?: string }>(({ theme, withoutscroll }) => ({
	color: theme.palette.error.main,
	marginRight: "-15px",
	marginLeft: "20px",
	marginBottom: withoutscroll === "true" ? "0px" : "32px"
}));

export const CoAppDialogTitleIconAndTextContainer = styled("div")(() => ({
	display: "inline-flex",
	alignItems: "center"
}));

// Basic table cell — used in confirmation modals and a few summary tables.
export const CoAppBasicTableCell = styled(TableCell)(({ theme }) => ({
	fontSize: 14,
	letterSpacing: "0.01em",
	color: "rgba(13, 31, 48, 0.85)",
	borderBottom: "1px solid rgba(13, 31, 48, 0.06)",
	[`&.${tableCellClasses.body}`]: {
		fontSize: 14,
		fontWeight: 500
	},
	[`&.${tableCellClasses.head}`]: {
		fontSize: 12,
		fontWeight: 600,
		color: "rgba(13, 31, 48, 0.55)",
		textTransform: "uppercase",
		letterSpacing: "0.06em"
	},
	padding: "12px 16px",
	[theme.breakpoints.down(1900)]: {
		paddingTop: "10px",
		paddingBottom: "10px"
	}
}));

export const CoAppBasicTableRow = styled(TableRow, {
	shouldForwardProp: (prop) => prop !== "withoutscroll",
})<{ withoutscroll?: string }>(({ theme, withoutscroll }) => ({
	backgroundColor: theme.palette.background.tableRow,
	"&:last-child td, &:last-child th": {
		border: withoutscroll === "true" ? "" : 0,
	}
}));

export const CoAppBasicTableContainer = styled(TableContainer, {
	shouldForwardProp: (prop) => prop !== "withoutscroll",
})<{ withoutscroll?: string }>(({ theme, withoutscroll }) => ({
	[theme.breakpoints.down(1900)]: {
		maxHeight: "450px"
	},
	maxHeight: "500px",
	borderRadius: withoutscroll === "true" ? "0px" : "0px 0px 8px 8px",
	overflowX: "hidden",
	overflowY: "auto",
	"&::-webkit-scrollbar": {
		backgroundColor: "#FFFFFF",
		width: "12px",
		borderRadius: "0px 0px 14px 0px"
	},
	"&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
		borderRadius: "999px",
		backgroundColor: "rgba(2, 213, 171, 0.55)",
		border: "3px solid #FFFFFF",
		backgroundClip: "content-box"
	}
}));

// Top-right user avatar (initials in a colored circle). Brand gradient
// background, white text, soft inner highlight to match new <Avatar/> primitive.
export const CoAppProfileAvatar = styled("div", {
	shouldForwardProp: (prop) => !["h", "w", "fs"].includes(prop as string),
})<{ h?: string; w?: string; fs?: string }>(({ h, w, fs = "" }) => ({
	color: "#FFFFFF",
	borderRadius: "50%",
	background: "var(--coapp-gradient-primary, linear-gradient(135deg, #02D5AB 0%, #0A7FA8 100%))",
	width: w ? w : "36px",
	textAlign: "center",
	lineHeight: h ? h : "36px",
	fontSize: fs ? fs : "14px",
	fontWeight: 600,
	letterSpacing: "0.02em",
	height: h ? h : "36px",
	verticalAlign: "middle",
	boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.18), 0 1px 3px rgba(13, 31, 48, 0.08)",
	userSelect: "none"
}));

export const CoAppProfileAvatarContainer = styled("div")(() => ({
	display: "flex",
	justifyContent: "center",
	alignItems: "center",
	height: "100%",
	paddingRight: "16px",
	cursor: "pointer",
	gap: 4
}));

// Logout text button at the bottom of the profile menu — destructive accent.
export const CoAppProfileLogoutTextButton = styled(Button)(() => ({
	color: "#E03030",
	width: "100%",
	justifyContent: "flex-start",
	padding: "10px 16px",
	textTransform: "none",
	fontSize: 14,
	fontWeight: 600,
	letterSpacing: "0.02em",
	borderRadius: 8,
	transition: "background 0.18s, color 0.18s",
	":hover": {
		color: "#CC2C2A",
		background: "rgba(224, 48, 48, 0.06)"
	}
}));

// Caret next to the avatar / inside expandable rows.
export const CoAppExpandMoreIcon = styled(ExpandMore, {
	shouldForwardProp: (prop) => !["clickedopen", "usefloat"].includes(prop as string),
})<{ clickedopen?: string; usefloat?: string | null }>(({ clickedopen, usefloat = null }) => ({
	color: "rgba(13, 31, 48, 0.45)",
	float: usefloat ? "right" : "none",
	cursor: "pointer",
	transform: clickedopen === "true" ? "rotate(180deg)" : "rotate(0deg)",
	transition: "transform 0.18s ease, color 0.18s",
	":hover": {
		color: "#02D5AB"
	}
}));

// Edit pencil next to user name in the profile menu.
export const CoAppEditIcon = styled(Edit)(() => ({
	color: "#02D5AB",
	cursor: "pointer",
	width: 18,
	height: 18,
	transition: "color 0.18s, transform 0.18s",
	":hover": {
		color: "#00B899",
		transform: "scale(1.08)"
	}
}));

// "+N" overflow indicator on group/role list text.
export const CoAppProfileListText = styled(Typography, {
	shouldForwardProp: (prop) => prop !== "remaindercount",
})<{ remaindercount?: number }>(({ remaindercount }) => ({
	fontSize: 13,
	fontWeight: 500,
	color: "rgba(13, 31, 48, 0.78)",
	lineHeight: 1.5,
	"&::after": {
		content: remaindercount ? `" +${remaindercount}"` : null,
		color: "#02D5AB",
		fontWeight: 600,
		marginLeft: 4
	}
}));

// Group / Roles section card inside the profile menu.
// Uses a soft surface with a subtle border, brand-tinted only on hover.
export const CoAppProfileResourceContainer = styled("div")(() => ({
	background: "rgba(13, 31, 48, 0.03)",
	color: "rgba(13, 31, 48, 0.85)",
	borderRadius: 10,
	border: "1px solid rgba(13, 31, 48, 0.06)",
	padding: "10px 12px",
	maxHeight: 320,
	overflowY: "auto",
	transition: "background 0.18s, border-color 0.18s",
	"&::-webkit-scrollbar": {
		background: "#FFFFFF",
		width: 12
	},
	"&::-webkit-scrollbar-thumb": {
		borderRadius: 999,
		background: "rgba(2, 213, 171, 0.55)",
		border: "3px solid #FFFFFF",
		backgroundClip: "content-box"
	}
}));

// The popover container itself — the menu card that drops below the avatar.
export const CoAppProfileContentContainer = styled("div")(() => ({
	background: "#FFFFFF",
	borderRadius: 12,
	position: "absolute",
	width: 320,
	top: 64,
	right: 16,
	zIndex: 999,
	overflow: "hidden",
	border: "1px solid rgba(13, 31, 48, 0.06)",
	boxShadow: "0 12px 32px rgba(13, 31, 48, 0.14), 0 4px 8px rgba(13, 31, 48, 0.06)"
}));

// User's display name in the menu header.
export const CoAppProfileNameText = styled("div")(() => ({
	fontSize: 15,
	fontWeight: 600,
	color: "rgba(13, 31, 48, 0.92)",
	lineHeight: 1.4,
	letterSpacing: "-0.005em",
	flex: 1,
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap"
}));

// Organization name (CoApp super admin only) — small caps style.
export const CoAppProfileOrgNameText = styled("div")(() => ({
	fontSize: 11,
	fontWeight: 700,
	color: "#02D5AB",
	lineHeight: 1.4,
	letterSpacing: "0.06em",
	textTransform: "uppercase"
}));

// Email under the user's name.
export const CoAppProfileEmailText = styled("div")(() => ({
	fontSize: 12,
	fontWeight: 500,
	color: "rgba(13, 31, 48, 0.55)",
	lineHeight: 1.5,
	letterSpacing: "0.01em",
	marginLeft: 16,
	marginRight: 16,
	marginBottom: 12,
	marginTop: -2,
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap"
}));

export const CoAppNameCell = styled("div")(() => ({
	display: "flex",
	flexDirection: "column",
	alignItems: "flex-start",
	// Fill the cell instead of a fixed 100px box. The old fixed width clipped
	// longer emails (e.g. "jack.lipchik@coapp.com") under the name. Letting the
	// cell size to its content lets the grid's autosize give the Name column
	// enough width to show the full email.
	width: "100%",
	minWidth: 0,
}));

// User profile detail page — flat, in-plane.
export const CoAppUserProfileMain = styled("div")(({ theme }) => ({
	borderRadius: 0,
	background: "#FFFFFF",
	width: "1565px",
	[theme.breakpoints.up(1900)]:        { minHeight: "40%", width: "1565px" },
	[theme.breakpoints.between(1600, 1900)]: { minHeight: "40%", width: "1440px" },
	[theme.breakpoints.between(1500, 1600)]: { minHeight: "40%", width: "1155px" },
	[theme.breakpoints.between(1000, 1500)]: { minHeight: "40%", width: "1160px" },
	[theme.breakpoints.down(1400)]:      { minHeight: "40%", width: "940px" },
	position: "absolute",
	top: 64,
	left: SIDEBAR_OFFSET_VAR,
	transition: sidebarLayoutTransition(theme),
	borderLeft: "1px solid rgba(13, 31, 48, 0.06)",
	overflow: "hidden"
}));

// Organization Settings page wrapper — flat, in-plane.
export const CoAppOrganizationSettingsMain = styled("div")(({ theme }) => ({
	borderRadius: 0,
	background: "#FFFFFF",
	width: "1565px",
	[theme.breakpoints.up(1900)]:        { minHeight: "60%", width: "1565px" },
	[theme.breakpoints.between(1600, 1900)]: { minHeight: "60%", width: "1440px" },
	[theme.breakpoints.between(1500, 1600)]: { minHeight: "60%", width: "1155px" },
	[theme.breakpoints.between(1000, 1500)]: { minHeight: "60%", width: "1160px" },
	[theme.breakpoints.down(1400)]:      { minHeight: "60%", width: "940px" },
	position: "absolute",
	top: 64,
	left: SIDEBAR_OFFSET_VAR,
	transition: sidebarLayoutTransition(theme),
	borderLeft: "none",
	overflow: "hidden"
}));

/**
 * width is calculated based on the positioning of the CoAppMain component absolutely (left: var(--sidebar-offset, 338px)) + 20px for padding on right
 * height is calculated based on the positioning of the CoAppMain component absolutely (top: 100px) + 20px for padding on bottom
 */
// Page content wrapper for data-grid-style list pages (rules library, users,
// roles, groups, plugins, workstations). Flat — sits in the same plane as
// the sidebar with just a hairline border, no card-elevation shadow.
export const CoAppDataGridMain = styled("div")(({ theme }) => ({
	borderRadius: 0,
	background: "#FFFFFF",
	width: SIDEBAR_WIDTH_CALC,
	height: "calc(100% - 64px)",
	position: "absolute",
	top: 64,
	left: SIDEBAR_OFFSET_VAR,
	transition: sidebarLayoutTransition(theme),
	// Edge-to-edge canvas. The page header + search row + grid each apply
	// their own horizontal padding so the bottom action footer can still
	// span flush from sidebar to right edge.
	paddingLeft: 0,
	paddingRight: 0,
	borderLeft: "none",
	overflow: "hidden"
}));

export const CoAppDataGridContainer = styled("div", {
	shouldForwardProp: (prop) => prop !== "height",
})<{ height?: string }>(({ height }) => ({
	// Fall back to a bounded height for BOTH undefined and "" (empty string).
	// An empty string would otherwise leave the grid unbounded on first paint,
	// detaching its virtualization scroller into a floating ghost panel.
	height: height || "620px",
	position: "relative",
	borderBottom: "1px solid rgba(13, 31, 48, 0.08)",
	borderRadius: "0 0 16px 16px",
	overflow: "hidden"
}));

// Toolbar above the data grid — search input + columns picker + bulk actions
// + pagination. Sits inside the wrapper with the same 24px horizontal padding
// as the page header so the search input and "Rules" title line up vertically.
export const CoAppDataGridToolbar = styled(Grid)(() => ({
	position: "relative",
	marginTop: 12,
	marginBottom: 8,
	paddingLeft: 24,
	paddingRight: 24,
	gap: 12,
	display: "flex",
	alignItems: "center",
	flexWrap: "wrap",
	// Re-style the embedded GridToolbarQuickFilter (MUI ships it as a
	// variant="standard" input — we want a pill-outlined input that matches
	// the rest of the design system).
	"& .MuiDataGrid-toolbarQuickFilter": {
		paddingTop: 0,
		paddingBottom: 0,
		"& .MuiInput-root": {
			background: "#FFFFFF",
			border: "1px solid rgba(13, 31, 48, 0.14)",
			borderRadius: 999,
			padding: "6px 14px 6px 12px",
			height: 38,
			transition: "border-color 0.18s ease, box-shadow 0.18s ease",
			"&:hover": {
				borderColor: "rgba(2, 213, 171, 0.55)"
			},
			"&.Mui-focused": {
				borderColor: "#02D5AB",
				boxShadow: "0 0 0 3px rgba(2, 213, 171, 0.18)"
			},
			// Kill the underline that ships with variant="standard".
			"&:before, &:after, &:hover:not(.Mui-disabled, .Mui-error):before": {
				borderBottom: "none !important"
			}
		},
		"& .MuiInput-input": {
			padding: 0,
			fontSize: 13,
			color: "rgba(13, 31, 48, 0.86)",
			"&::placeholder": {
				color: "rgba(13, 31, 48, 0.45)",
				opacity: 1
			}
		},
		"& .MuiSvgIcon-root": {
			color: "rgba(13, 31, 48, 0.45)",
			fontSize: 18
		}
	}
}));

export const CoAppDataGridPaginationContainer = styled(Grid)(() => ({
	display: "flex",
	alignItems: "center",
	marginLeft: 8,
	"& .MuiTablePagination-root": {
		minHeight: 0,
		overflow: "visible"
	},
	"& .MuiTablePagination-toolbar": {
		minHeight: 0,
		paddingLeft: 0,
		paddingRight: 0
	},
	"& .MuiTablePagination-displayedRows": {
		fontSize: 13,
		color: "rgba(13, 31, 48, 0.55)",
		fontWeight: 500
	},
	"& .MuiTablePagination-actions": {
		marginLeft: 8
	}
}));

export const CoAppDataGridAllSelectedContainer = styled("div")(({ theme }) => ({
	display: "flex",
	alignContent: "center",
	alignItems: "center",
	justifyContent: "center",
	background: "rgba(2, 213, 171, 0.08)",
	color: "rgba(13, 31, 48, 0.85)",
	width: "100%",
	height: 52,
	marginTop: 8,
	gap: 8,
	borderRadius: 10,
	fontSize: 13,
	fontWeight: 500,
	border: "1px solid rgba(2, 213, 171, 0.18)",
	letterSpacing: "0.01em"
}));

// Three-dot row action button — used inside data grid rows.
export const CoAppDataGridActionsContainer = styled(IconButton)(() => ({
	width: 32,
	height: 32,
	color: "rgba(13, 31, 48, 0.45)",
	transition: "background 0.18s, color 0.18s",
	":hover": {
		background: "rgba(2, 213, 171, 0.10)",
		color: "#02D5AB"
	}
}));

// Page H1 inside CoAppDatagridHeader (Rules / Users / Groups / Roles / etc.).
export const CoAppDataGridHeaderTitle = styled(Typography)(({ theme }) => ({
	fontSize: 26,
	fontWeight: 600,
	letterSpacing: "-0.01em",
	color: "rgba(13, 31, 48, 0.92)",
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap",
	[theme.breakpoints.between(1600, 2000)]: { maxWidth: "700px" },
	[theme.breakpoints.between(1500, 1600)]: { maxWidth: "600px" },
	[theme.breakpoints.between(1000, 1400)]: { maxWidth: "400px" },
}));

export const CoAppSuperAdminTextCell = styled("div", {
	shouldForwardProp: (prop) => !["after", "before"].includes(prop as string),
})<{ after?: string; before?: string }>(({ theme }) => ({
	fontSize: theme.typography.body2.fontSize,
	height: "50px",
	alignContent: "center",
	lineHeight: "16px",
	"&::after": {
		content: "' CoApp Support'",
		letterSpacing: "0.17px",
		color: theme.palette.secondary.main,
		fontWeight: theme.typography.fontWeightBold,
		fontSize: theme.typography.body2.fontSize
	},
	marginBottom: "-15px",
	marginTop: "-12px"
}));

export const CoAppCopyBlockContainer = styled(Box)(({ theme }) => ({
	backgroundColor: theme.palette.black_states.focus,
	width: "fit-content",
	padding: "10px",
	borderRadius: "4px"
}));

export const CoAppCopyBlockTypography = styled(Typography)(({ theme }) => ({
	paddingRight: "10px",
	maxWidth: "750px",
	overflowWrap: "break-word"
}));

export const CoAppHelpIcon = styled(Help)(({ theme }) => ({
	color: theme.palette.info.main,
	cursor: "pointer"
}));

export const CoAppPlayArrow = styled(PlayArrow, {
	shouldForwardProp: (prop) => !["top", "left"].includes(prop as string),
})<{ top?: number; left?: number }>(({ theme, top, left }) => ({
	position: "absolute",
	backgroundColor: theme.palette.primary.main,
	color: theme.palette.white_states.main,
	width: "60px",
	height: "60px",
	top: top ? top : 48,
	left: left ? left : 122,
	borderRadius: "50%",
	cursor: "pointer"
}));

type CoAppThumbnailTooltipRootProps = CoAppStyledTooltipRootProps & {
	/** Style-only; not forwarded to MUI `Tooltip`. */
	opacity?: string;
};

function CoAppThumbnailTooltipRoot({ className, opacity: _opacity, ...props }: CoAppThumbnailTooltipRootProps) {
	return (
		<Tooltip
			{...props}
			arrow
			placement={props.placement ?? "bottom"}
			classes={{ popper: className }}
		/>
	);
}

export const CoAppThumbnailTooltip = styled(CoAppThumbnailTooltipRoot)(({ theme, opacity, placement }) => ({
	[`& .${tooltipClasses.tooltip}`]: {
		backgroundColor: opacity === "true" ? "#FDFDFDE5" : "#FDFDFDF2",
		paddingTop: "4px",
		paddingBottom: "4px",
		maxWidth: "400px",
		border: `2px solid ${theme.palette.primary.main}`,
	},
	[`& .${tooltipClasses.arrow}`]: {
		"&::before": {
			backgroundColor: "#FDFDFDF2",
			boxShadow: placement === "bottom" ? "2px 2px 2px 2px rgba(0, 0, 0, 0.33)" : "2px 0px 2px 2px rgba(0, 0, 0, 0.33)"
		}
	}
}));

export const CoAppEmbeddedVideoContainer = styled("div")(({ theme }) => ({
	position: "relative",
	height: "700px"
}));

export const CoAppEmbeddedVideoLoadingContainer = styled("div")(({ theme }) => ({
	position: "absolute",
	top: 350,
	left: 900
}));

export const CoAppEmbeddedVideoIframe = styled("iframe")(({ theme }) => ({
	position: "absolute",
	top: 0,
	left: 0,
	width: "100%",
	height: "100%"
}));

export const CoAppThumbnailOverlayText = styled(Typography)(({ theme }) => ({
	fontWeight: 600,
	position: "absolute",
	top: 0,
	left: 50,
	width: "225px",
	textAlign: "center"
}));

export const SsoDomainContainer = styled("div")(() => ({
	width: "50%"
}));
